import { useEffect, useState } from "react";
import { Plus, Trash2, Ticket, Loader2, Power, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { code: "", allStructures: true, property_ids: [], type: "percent", value: 10, expiry: "", max_uses: "", show_banner: false, banner_text: "" };

export default function AdminDiscountCodes() {
  const [codes, setCodes] = useState([]);
  const [props, setProps] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([api.get("/admin/discount-codes"), api.get("/admin/apartments")])
    .then(([c, p]) => { setCodes(c.data); setProps(p.data); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const propTitle = (id) => props.find((p) => p.id === id)?.title || "—";
  const pidsOf = (c) => c.property_ids ?? (c.property_id ? [c.property_id] : []);

  const toggleStructure = (id) => setForm((f) => ({
    ...f, property_ids: f.property_ids.includes(id) ? f.property_ids.filter((x) => x !== id) : [...f.property_ids, id],
  }));

  const create = async () => {
    if (!form.code.trim()) return toast.error("Inserisci un codice");
    if (!form.allStructures && form.property_ids.length === 0) return toast.error("Seleziona almeno una struttura");
    setSaving(true);
    try {
      await api.post("/admin/discount-codes", {
        code: form.code.trim().toUpperCase(),
        property_ids: form.allStructures ? [] : form.property_ids,
        type: form.type, value: Number(form.value), expiry: form.expiry || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        show_banner: form.show_banner, banner_text: form.banner_text || "",
      });
      toast.success("Codice sconto creato");
      setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/admin/discount-codes/${c.id}`, { active: !(c.active !== false) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleBanner = async (c) => {
    try { await api.put(`/admin/discount-codes/${c.id}`, { show_banner: !c.show_banner }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo codice sconto?")) return;
    await api.delete(`/admin/discount-codes/${id}`); toast.success("Eliminato"); load();
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-4xl text-fir flex items-center gap-2"><Ticket className="text-gold" /> Codici Sconto</h1>
        <p className="text-fir-deep/60">Crea codici sconto validi per tutte le strutture o solo per alcune. Il cliente li inserisce in fase di prenotazione.</p>
      </div>

      <div className="bg-white rounded-2xl border border-stone p-6 mb-8" data-testid="discount-create-form">
        <h2 className="font-display text-2xl text-fir mb-4">Nuovo codice</h2>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Codice</label>
            <input data-testid="discount-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="ES. INVERNO25" className="input-alpine w-full mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Tipo</label>
            <select data-testid="discount-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-alpine w-full mt-1">
              <option value="percent">Percentuale %</option>
              <option value="amount">Importo € fisso</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Valore ({form.type === "percent" ? "%" : "€"})</label>
            <input data-testid="discount-value-input" type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="input-alpine w-full mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Scadenza (opz.)</label>
            <input data-testid="discount-expiry-input" type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className="input-alpine w-full mt-1" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Limite utilizzi (opz.)</label>
            <input data-testid="discount-maxuses-input" type="number" min="0" value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Es. 10 (le prime 10 prenotazioni)" className="input-alpine w-full mt-1" />
            <p className="text-[11px] text-fir-deep/45 mt-1">Lascia vuoto per utilizzi illimitati. Il conteggio aumenta a ogni prenotazione confermata.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Testo banner (opz.)</label>
            <input data-testid="discount-bannertext-input" value={form.banner_text}
              onChange={(e) => setForm({ ...form, banner_text: e.target.value })} placeholder="Es. Offerta lancio inverno!" className="input-alpine w-full mt-1" />
          </div>
        </div>

        <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="discount-showbanner-checkbox">
          <input type="checkbox" checked={form.show_banner} onChange={(e) => setForm({ ...form, show_banner: e.target.checked })} className="accent-gold w-4 h-4" />
          <span className="text-fir">Mostra banner promozionale in homepage</span>
        </label>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wide text-fir-deep/60">Valido per</label>
          <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="discount-all-structures">
            <input type="checkbox" checked={form.allStructures} onChange={(e) => setForm({ ...form, allStructures: e.target.checked })} className="accent-gold w-4 h-4" />
            <span className="text-fir">Tutte le strutture</span>
          </label>
          {!form.allStructures && (
            <div className="grid sm:grid-cols-2 gap-2 mt-3 pl-6" data-testid="discount-structure-list">
              {props.map((p) => (
                <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm text-fir-deep/90">
                  <input type="checkbox" data-testid={`discount-structure-${p.id}`} checked={form.property_ids.includes(p.id)} onChange={() => toggleStructure(p.id)} className="accent-gold w-4 h-4" />
                  <span>{p.type === "hotel" ? "🏨 " : "🏠 "}{p.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <button data-testid="discount-create-btn" onClick={create} disabled={saving} className="gold-btn px-5 py-3 rounded-xl flex items-center gap-2 mt-5">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <><Plus size={18} /> Crea codice</>}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-stone overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone/40 text-fir-deep/70">
            <tr>
              <th className="text-left p-3">Codice</th>
              <th className="text-left p-3">Strutture</th>
              <th className="text-left p-3">Sconto</th>
              <th className="text-left p-3">Utilizzi</th>
              <th className="text-left p-3">Scadenza</th>
              <th className="text-left p-3">Banner</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-right p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => {
              const active = c.active !== false;
              const expired = c.expiry && c.expiry < new Date().toISOString().slice(0, 10);
              const pids = pidsOf(c);
              return (
                <tr key={c.id} data-testid="discount-code-row" className="border-t border-stone">
                  <td className="p-3 font-mono font-semibold text-fir">{c.code}</td>
                  <td className="p-3 text-fir-deep/80">{pids.length === 0 ? <span className="text-gold font-medium">Tutte</span> : pids.map(propTitle).join(", ")}</td>
                  <td className="p-3">{c.type === "percent" ? `-${c.value}%` : `-€${c.value}`}</td>
                  <td className="p-3 text-fir-deep/80" data-testid="discount-usage">
                    {c.max_uses ? <span className={(c.used_count || 0) >= c.max_uses ? "text-red-600 font-medium" : ""}>{c.used_count || 0}/{c.max_uses}</span> : `${c.used_count || 0} · ∞`}
                  </td>
                  <td className="p-3">{c.expiry ? <span className={expired ? "text-red-600" : ""}>{c.expiry}{expired ? " (scaduto)" : ""}</span> : "—"}</td>
                  <td className="p-3">
                    <button data-testid="discount-banner-toggle" onClick={() => toggleBanner(c)}
                      className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${c.show_banner ? "bg-gold/20 text-wood" : "bg-stone text-fir-deep/60 hover:bg-stone/70"}`}>
                      <Megaphone size={12} /> {c.show_banner ? "Attivo" : "Off"}
                    </button>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-green-100 text-green-700" : "bg-stone text-fir-deep/60"}`}>{active ? "Attivo" : "Disattivo"}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid="discount-toggle-btn" onClick={() => toggle(c)} className="text-fir hover:text-gold inline-flex items-center gap-1 mr-3"><Power size={14} /> {active ? "Disattiva" : "Attiva"}</button>
                    <button data-testid="discount-delete-btn" onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {codes.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-fir-deep/50">Nessun codice sconto. Creane uno qui sopra.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
